// src/features/infoscreens/pages/CreateInfoscreenPage.jsx
import React, { useState, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import {
  Box,
  Typography,
  Alert,
  TextField,
  ToggleButton,
  ToggleButtonGroup,
  FormControlLabel,
  Switch,
} from '@mui/material';
import {
  Save as SaveIcon,
  ViewList as ViewListIcon,
  ViewModule,
  AccountBox as AccountBoxIcon,
} from '@mui/icons-material';
import { useNavigate } from 'react-router-dom';
import { INFOSCREENS_LIST_PATH } from '../paths';

// Hooks
import { useInfoscreens } from '../hooks/useInfoscreens';
import { useSnackbar } from '../../../hooks/mui/useSnackBar';
import { useNavigationStack } from '../../../context/NavigationStackContext';
import { getCreatedId } from '../../../utils/helpers/getCreatedId';
import { FormPage, FormSection, FormActions } from '../../../components/common/form';

export default function CreateInfoscreenPage() {
  const { t } = useTranslation(['infoscreens', 'common']);
  const navigate = useNavigate();
  const { showSuccess, showError } = useSnackbar();
  const { navigateBack } = useNavigationStack();

  // State
  const [formData, setFormData] = useState({
    name: '',
    login_code: '',
    viewmode: 'card',
    auto_fullscreen: true,
  });
  const [validationErrors, setValidationErrors] = useState({});

  // API hooks
  const {
    createInfoscreen,
    loading,
    error,
    clearError,
  } = useInfoscreens();

  // Form handling
  const handleInputChange = useCallback((field, value) => {
    setFormData(prev => ({ ...prev, [field]: value }));

    // Clear validation error for this field
    setValidationErrors(prev => {
      if (prev[field]) {
        const { [field]: removed, ...rest } = prev;
        return rest;
      }
      return prev;
    });
  }, []);

  const validateForm = useCallback(() => {
    const errors = {};

    if (!formData.name.trim()) {
      errors.name = t('infoscreens:validation.name_required');
    } else if (formData.name.length < 2) {
      errors.name = t('infoscreens:validation.name_min_length');
    } else if (formData.name.length > 255) {
      errors.name = t('infoscreens:validation.name_max_length');
    }

    if (!formData.login_code.trim()) {
      errors.login_code = t('infoscreens:validation.login_code_required');
    } else if (formData.login_code.length < 4) {
      errors.login_code = t('infoscreens:validation.login_code_min_length');
    } else if (formData.login_code.length > 255) {
      errors.login_code = t('infoscreens:validation.login_code_max_length');
    }

    setValidationErrors(errors);
    return Object.keys(errors).length === 0;
  }, [formData, t]);

  const handleSubmit = useCallback(async (event) => {
    event.preventDefault();

    if (!validateForm()) {
      return;
    }

    try {
      const infoscreenData = {
        name: formData.name.trim(),
        login_code: formData.login_code.trim(),
        viewmode: formData.viewmode,
        auto_fullscreen: formData.auto_fullscreen,
      };

      const result = await createInfoscreen(infoscreenData);

      if (result.success) {
        showSuccess(t('infoscreens:success.infoscreen_created'));
        // Open the new record (replace, so Back skips the filled-in form)
        const newId = getCreatedId(result, 'infoscreen_id');
        navigate(newId ? `/infoscreens/${newId}` : INFOSCREENS_LIST_PATH, { replace: true });
      } else {
        showError(result.error || t('infoscreens:errors.create_failed'));
      }
    } catch (error) {
      showError(t('infoscreens:errors.unexpected_error'));
    }
  }, [validateForm, formData, createInfoscreen, showSuccess, showError, navigate, t]);

  const handleCancel = useCallback(() => {
    navigateBack(INFOSCREENS_LIST_PATH);
  }, [navigateBack]);

  return (
    <FormPage
      title={t('infoscreens:add_infoscreen')}
      subtitle={t('infoscreens:form.create_subtitle')}
      onBack={handleCancel}
      onSubmit={handleSubmit}
      fieldErrors={validationErrors}
      error={error}
      onErrorClose={clearError}
    >
      <FormSection title={t('infoscreens:form.infoscreen_information')}>
        <TextField
          fullWidth
          label={t('infoscreens:labels.name')}
          value={formData.name}
          onChange={(e) => handleInputChange('name', e.target.value)}
          error={!!validationErrors.name}
          helperText={validationErrors.name || t('infoscreens:form.name_helper')}
          required
          disabled={loading}
          placeholder={t('infoscreens:placeholders.name')}
          inputProps={{ maxLength: 255 }}
        />

        <TextField
          fullWidth
          label={t('infoscreens:labels.login_code')}
          value={formData.login_code}
          onChange={(e) => handleInputChange('login_code', e.target.value)}
          error={!!validationErrors.login_code}
          helperText={validationErrors.login_code || t('infoscreens:form.login_code_helper')}
          required
          disabled={loading}
          placeholder={t('infoscreens:placeholders.login_code')}
          inputProps={{ maxLength: 255 }}
        />

        <Box>
          <Typography variant="subtitle2" gutterBottom>
            {t('infoscreens:labels.viewmode')}
          </Typography>
          <ToggleButtonGroup
            value={formData.viewmode}
            exclusive
            onChange={(e, newValue) => {
              if (newValue !== null) {
                handleInputChange('viewmode', newValue);
              }
            }}
            disabled={loading}
            sx={{ mt: 1 }}
          >
            <ToggleButton value="card" sx={{ py: 2, display: 'flex', flexDirection: 'column', gap: 1, minWidth: 100 }}>
              <ViewModule fontSize="large" />
              <Typography variant="body2">
                {t('infoscreens:viewmodes.card')}
              </Typography>
            </ToggleButton>
            <ToggleButton value="list" sx={{ py: 2, display: 'flex', flexDirection: 'column', gap: 1, minWidth: 100 }}>
              <ViewListIcon fontSize="large" />
              <Typography variant="body2">
                {t('infoscreens:viewmodes.list')}
              </Typography>
            </ToggleButton>
            <ToggleButton value="user" sx={{ py: 2, display: 'flex', flexDirection: 'column', gap: 1, minWidth: 100 }}>
              <AccountBoxIcon fontSize="large" />
              <Typography variant="body2">
                {t('infoscreens:viewmodes.user')}
              </Typography>
            </ToggleButton>
          </ToggleButtonGroup>
        </Box>

        <FormControlLabel
          control={
            <Switch
              checked={formData.auto_fullscreen}
              onChange={(e) => handleInputChange('auto_fullscreen', e.target.checked)}
              disabled={loading}
            />
          }
          label={
            <Box>
              <Typography variant="body2">{t('infoscreens:labels.auto_fullscreen')}</Typography>
              <Typography variant="caption" color="text.secondary">
                {t('infoscreens:form.auto_fullscreen_helper')}
              </Typography>
            </Box>
          }
        />
        <Alert severity="info">
          {t('infoscreens:form.after_creation_info')}
        </Alert>
      </FormSection>
      <FormActions
        onCancel={handleCancel}
        submitType="submit"
        submitLabel={loading ? t('infoscreens:actions.creating') : t('infoscreens:actions.create_infoscreen')}
        submitIcon={<SaveIcon />}
        loading={loading}
      />
    </FormPage>
  );
}
